import { DateSelectArg } from '@fullcalendar/core';
import {
  Goal,
  ID,
  LifeArea,
  LinkOptions,
  SmartList,
  Tag,
  TaskActionType,
  Timeslot,
} from '@griply/types';
import {
  getCalendarTaskDuration,
  getCalendarTaskStartTime,
} from '@griply/utils';
import { endOfDay } from 'date-fns';
import { useCallback, useState } from 'react';
import { useOpenCreateTask } from 'shared/contexts/task-detail';

import { useCreateTimeslotTask } from './use-create-timeslot-task';

const defaultClickDurationMs = 60 * 60 * 1000;

type CalendarSlotMenuState = {
  date: Date;
  timeslot: Timeslot | null;
  anchor: { top: number; left: number };
  unselect?: () => void;
};

// Drag-select / click opens the slot menu at the cursor; holding Alt bypasses it straight to the New task dialog.
export const useCalendarSlotMenu = (
  lifeAreas: LifeArea[],
  goals: Goal[],
  tags: Tag[],
  smartLists: SmartList[],
) => {
  const openCreateTask = useOpenCreateTask();
  const createTimeslotTask = useCreateTimeslotTask();

  const [slotMenu, setSlotMenu] = useState<CalendarSlotMenuState | null>(null);

  const openCreateTaskForSlot = useCallback(
    (date: Date, timeslot: Timeslot | null) => {
      openCreateTask(
        {
          timeslot,
          endStrategy: { completionCount: null, deadline: endOfDay(date) },
        },
        { dateAnchored: true },
      );
    },
    [openCreateTask],
  );

  const onSelect = useCallback(
    ({ start, end, jsEvent, view }: DateSelectArg) => {
      const mouseEvent = jsEvent as MouseEvent | null;
      const timeslot: Timeslot = {
        startTime: getCalendarTaskStartTime(start),
        duration: getCalendarTaskDuration(start, end),
      };

      if (mouseEvent?.altKey) {
        openCreateTaskForSlot(start, timeslot);
        view.calendar.unselect();
        return;
      }

      setSlotMenu({
        date: start,
        timeslot,
        anchor: {
          top: mouseEvent?.clientY ?? 0,
          left: mouseEvent?.clientX ?? 0,
        },
        unselect: () => view.calendar.unselect(),
      });
    },
    [openCreateTaskForSlot],
  );

  const onDateTime = useCallback(
    (info: { date: Date; allDay: boolean; jsEvent?: MouseEvent }) => {
      const { date, allDay, jsEvent } = info;
      const timeslot: Timeslot | null = allDay
        ? null
        : {
            startTime: getCalendarTaskStartTime(date),
            duration: defaultClickDurationMs,
          };

      if (jsEvent?.altKey) {
        openCreateTaskForSlot(date, timeslot);
        return;
      }

      setSlotMenu({
        date,
        timeslot,
        anchor: { top: jsEvent?.clientY ?? 0, left: jsEvent?.clientX ?? 0 },
      });
    },
    [openCreateTaskForSlot],
  );

  const closeSlotMenu = useCallback(() => {
    // clears the FullCalendar selection highlight (drag path only)
    slotMenu?.unselect?.();
    setSlotMenu(null);
  }, [slotMenu]);

  const onNewTask = useCallback(() => {
    if (!slotMenu) {
      return;
    }
    openCreateTaskForSlot(slotMenu.date, slotMenu.timeslot);
    closeSlotMenu();
  }, [slotMenu, openCreateTaskForSlot, closeSlotMenu]);

  const onSelectLinkedItem = useCallback(
    (link: { id: ID; type: LinkOptions } | null) => {
      if (!slotMenu || !link) {
        closeSlotMenu();
        return;
      }

      if (link.type === LinkOptions.Goal) {
        const goal = goals.find(({ id }) => id === link.id);
        if (goal) {
          createTimeslotTask(
            {
              id: goal.id,
              name: goal.name,
              actionType: TaskActionType.GoalTimeslot,
            },
            slotMenu.date,
            slotMenu.timeslot,
          );
        }
        closeSlotMenu();
        return;
      }

      const lifeArea = lifeAreas.find(({ id }) => id === link.id);
      if (lifeArea) {
        createTimeslotTask(
          {
            id: lifeArea.id,
            name: lifeArea.name,
            actionType: TaskActionType.LifeAreaTimeslot,
          },
          slotMenu.date,
          slotMenu.timeslot,
        );
      }
      closeSlotMenu();
    },
    [slotMenu, goals, lifeAreas, createTimeslotTask, closeSlotMenu],
  );

  const onNewEvent = useCallback(() => {
    window.alert('Create event');
    closeSlotMenu();
  }, [closeSlotMenu]);

  const onSelectTag = useCallback(
    (tagId: ID) => {
      if (!slotMenu) {
        closeSlotMenu();
        return;
      }
      const tag = tags.find(({ id }) => id === tagId);
      if (tag) {
        createTimeslotTask(
          {
            id: tag.id,
            name: tag.name,
            actionType: TaskActionType.TagTimeslot,
          },
          slotMenu.date,
          slotMenu.timeslot,
        );
      }
      closeSlotMenu();
    },
    [slotMenu, tags, createTimeslotTask, closeSlotMenu],
  );

  const onSelectSmartList = useCallback(
    (smartListId: ID) => {
      if (!slotMenu) {
        closeSlotMenu();
        return;
      }
      const smartList = smartLists.find(({ id }) => id === smartListId);
      if (smartList) {
        createTimeslotTask(
          {
            id: smartList.id,
            name: smartList.name,
            actionType: TaskActionType.SmartListTimeslot,
          },
          slotMenu.date,
          slotMenu.timeslot,
        );
      }
      closeSlotMenu();
    },
    [slotMenu, smartLists, createTimeslotTask, closeSlotMenu],
  );

  const slotContextMenu = {
    slot: slotMenu,
    onClose: closeSlotMenu,
    onNewTask,
    onNewEvent,
    onSelectLinkedItem,
    onSelectTag,
    onSelectSmartList,
  };

  return { onSelect, onDateTime, slotContextMenu };
};
